import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, CheckCircle2, FileSpreadsheet, RefreshCw, Download, Unplug, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

const apiErr = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";

export function GoogleSheetsPanel({ campaignId = null, onImported }) {
  const [status, setStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [sheetUrl, setSheetUrl] = useState("");
  const [sheetId, setSheetId] = useState("");
  const [tabs, setTabs] = useState([]);
  const [tab, setTab] = useState("");
  const [loadingTabs, setLoadingTabs] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verify, setVerify] = useState(null); // {headers, mapping, missing, all_mapped, sample, fields}
  const [mapping, setMapping] = useState({});
  const [saveDefault, setSaveDefault] = useState(false);
  const [importing, setImporting] = useState(false);
  const [changing, setChanging] = useState(false);

  const loadStatus = useCallback(() => api.get("/integrations/google_sheets/status").then((r) => setStatus(r.data)).catch(() => {}), []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    const onMsg = (e) => {
      if (e.data === "sheets-connected") { toast.success("Google account connected!"); setConnecting(false); loadStatus(); }
      if (e.data === "sheets-error") { toast.error("Could not connect Google account"); setConnecting(false); }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [loadStatus]);

  const connect = async () => {
    setConnecting(true);
    let popup = null;
    try {
      const { data } = await api.get("/oauth/sheets/auth-url");
      const w = 520, h = 640;
      const left = window.screenX + (window.outerWidth - w) / 2, top = window.screenY + (window.outerHeight - h) / 2;
      popup = window.open(data.url, "gsheets_oauth", `width=${w},height=${h},left=${left},top=${top}`);
      if (!popup) { toast.error("Popup blocked — allow popups and retry"); setConnecting(false); return; }
    } catch (e) { toast.error(apiErr(e)); setConnecting(false); return; }
    // Poll the server for the connection result (postMessage can be blocked by Google's COOP).
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const s = await api.get("/integrations/google_sheets/status");
        if (s.data.connected) {
          clearInterval(iv); setConnecting(false); setStatus(s.data);
          toast.success(`Connected as ${s.data.email || "your Google account"}`);
          if (popup && !popup.closed) popup.close();
          return;
        }
      } catch (e) { console.debug("Sheets status poll (transient):", e?.message || e); }
      if (tries > 60 || (popup && popup.closed && tries > 2)) {
        clearInterval(iv); setConnecting(false); loadStatus();
        if (popup && popup.closed) toast.info("Popup closed. If you finished, click Connect again to refresh.");
      }
    }, 2000);
  };

  const loadTabs = async () => {
    if (!sheetUrl.trim()) { toast.error("Paste your Google Sheet URL"); return; }
    setLoadingTabs(true); setVerify(null);
    try {
      const { data } = await api.post("/integrations/google_sheets/tabs", { sheet_url: sheetUrl });
      setSheetId(data.sheet_id); setTabs(data.tabs || []); setTab(data.tabs?.[0] || "");
      toast.success(`Found ${data.tabs?.length || 0} tab(s) in "${data.sheet_name}"`);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoadingTabs(false); }
  };

  const doVerify = async () => {
    if (!sheetId || !tab) { toast.error("Load and pick a tab first"); return; }
    setVerifying(true);
    try {
      const { data } = await api.post("/integrations/google_sheets/verify", { sheet_id: sheetId, tab });
      setVerify(data); setMapping(data.mapping || {});
      if (data.all_mapped) toast.success("All columns mapped automatically");
      else toast.info("Some columns need mapping");
    } catch (e) { toast.error(apiErr(e)); } finally { setVerifying(false); }
  };

  const doImport = async () => {
    const req = (verify?.fields || []).filter((f) => f.required);
    const miss = req.filter((f) => !mapping[f.field]);
    if (miss.length) { toast.error(`Map required: ${miss.map((f) => f.label).join(", ")}`); return; }
    setImporting(true);
    try {
      const { data } = await api.post("/integrations/google_sheets/import", { sheet_id: sheetId, tab, mapping, campaign_id: campaignId, save_default: saveDefault });
      toast.success(data.message);
      setChanging(false); setVerify(null); setSheetUrl(""); loadStatus();
      onImported && onImported(data);
    } catch (e) { toast.error(apiErr(e)); } finally { setImporting(false); }
  };

  const syncNow = async () => {
    try { const { data } = await api.post("/integrations/google_sheets/sync-now", {}); toast.success(data.message); loadStatus(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Sheets? Your stored tokens will be revoked.")) return;
    try { await api.post("/integrations/google_sheets/disconnect", {}); toast.success("Disconnected"); setStatus(null); setVerify(null); loadStatus(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const downloadTemplate = () => { window.open(`${API}/integrations/google_sheets/template`, "_blank"); };

  const headerIndex = (h) => (verify?.headers || []).indexOf(h);

  if (!status) return <div className="py-4 text-center"><Loader2 className="w-5 h-5 animate-spin text-indigo-500 mx-auto" /></div>;

  // ---------- NOT CONNECTED ----------
  if (!status.connected) {
    return (
      <div className="space-y-4" data-testid="gs-not-connected">
        <div className="rounded-xl border p-4" style={{ borderColor: "rgba(16,185,129,0.35)", background: "rgba(16,185,129,0.05)" }}>
          <div className="flex items-center gap-2 mb-2"><FileSpreadsheet className="w-5 h-5 text-emerald-600" /><h3 className="font-semibold font-display">Connect Your Google Sheet</h3></div>
          <ol className="text-sm text-slate-600 dark:text-slate-300 space-y-1 list-decimal ml-5">
            <li>Click <b>Connect Google Account</b></li>
            <li>Sign in with Google and click <b>Allow</b></li>
            <li>Paste your Google Sheet URL</li>
            <li>We import your leads automatically</li>
          </ol>
        </div>
        {!status.configured && <div className="text-xs text-amber-600">Google OAuth is not configured on the platform. Contact support.</div>}
        <div className="flex gap-2">
          <Button onClick={connect} disabled={connecting || !status.configured} data-testid="gs-connect-btn">
            {connecting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileSpreadsheet className="w-4 h-4 mr-1" />}Connect Google Account
          </Button>
          <Button variant="outline" onClick={downloadTemplate} data-testid="gs-download-template"><Download className="w-4 h-4 mr-1" />Download Template</Button>
        </div>
      </div>
    );
  }

  // ---------- CONNECTED ----------
  const showPicker = changing || !status.google_sheet_id === true || !status.tab || verify;
  return (
    <div className="space-y-4" data-testid="gs-connected">
      <div className="rounded-xl border p-4 flex flex-wrap items-center gap-x-6 gap-y-2" style={{ borderColor: "rgba(16,185,129,0.35)", background: "rgba(16,185,129,0.05)" }}>
        <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-600" /><span className="text-sm font-medium" data-testid="gs-email">Connected as: {status.email || "Google account"}</span></div>
        {status.sheet_name && <span className="text-xs text-slate-500">Active Sheet: <b>{status.sheet_name}</b>{status.tab ? ` · ${status.tab}` : ""}</span>}
        {status.last_synced_at && <span className="text-xs text-slate-500">Last synced: {new Date(status.last_synced_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>}
        <div className="flex gap-2 ml-auto">
          <Button size="sm" variant="outline" onClick={syncNow} data-testid="gs-sync-now"><RefreshCw className="w-3.5 h-3.5 mr-1" />Sync Now</Button>
          <Button size="sm" variant="outline" onClick={() => { setChanging(true); setVerify(null); }} data-testid="gs-change-sheet">Change Sheet</Button>
          <Button size="sm" variant="ghost" onClick={disconnect} data-testid="gs-disconnect"><Unplug className="w-3.5 h-3.5 mr-1" />Disconnect</Button>
        </div>
      </div>

      {(changing || !status.google_sheet_id) && (
        <div className="space-y-3" data-testid="gs-setup">
          <div>
            <Label className="text-sm">Google Sheet URL</Label>
            <div className="flex gap-2 mt-1">
              <Input value={sheetUrl} onChange={(e) => setSheetUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/YOUR_SHEET_ID/edit" data-testid="gs-sheet-url" />
              <Button variant="outline" onClick={loadTabs} disabled={loadingTabs} data-testid="gs-load-tabs">{loadingTabs ? <Loader2 className="w-4 h-4 animate-spin" /> : "Load Tabs"}</Button>
            </div>
          </div>
          {tabs.length > 0 && (
            <div className="flex items-end gap-2">
              <div className="flex-1"><Label className="text-sm">Tab</Label>
                <Select value={tab} onValueChange={setTab}><SelectTrigger className="mt-1" data-testid="gs-tab-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{tabs.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              </div>
              <Button onClick={doVerify} disabled={verifying} data-testid="gs-verify">{verifying ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Verify Sheet</Button>
            </div>
          )}

          {verify && (
            <div className="space-y-3">
              {verify.all_mapped ? (
                <div className="text-sm text-emerald-600 flex items-center gap-1.5" data-testid="gs-all-mapped"><CheckCircle2 className="w-4 h-4" />All columns mapped automatically</div>
              ) : (
                <div className="rounded-lg border p-3" data-testid="gs-mapper">
                  <div className="font-semibold text-sm mb-2">Map Your Columns</div>
                  <div className="space-y-2">
                    {verify.fields.map((f) => (
                      <div key={f.field} className="grid grid-cols-2 gap-2 items-center">
                        <span className="text-sm">{f.label}{f.required ? " *" : ""}</span>
                        <Select value={mapping[f.field] || "__none__"} onValueChange={(v) => setMapping({ ...mapping, [f.field]: v === "__none__" ? "" : v })}>
                          <SelectTrigger data-testid={`gs-map-${f.field}`}><SelectValue placeholder="Choose column" /></SelectTrigger>
                          <SelectContent>
                            {!f.required && <SelectItem value="__none__">— skip —</SelectItem>}
                            {verify.headers.filter((h) => String(h).trim()).map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {verify.sample?.length > 0 && (
                <div className="rounded-lg border overflow-x-auto" data-testid="gs-preview">
                  <div className="px-3 py-2 text-xs font-semibold border-b bg-slate-50 dark:bg-slate-800">Preview (first {verify.sample.length} rows)</div>
                  <table className="w-full text-xs">
                    <thead><tr className="border-b text-slate-400">{verify.fields.map((f) => <th key={f.field} className="text-left px-3 py-1.5">{f.label}</th>)}</tr></thead>
                    <tbody>{verify.sample.map((row, i) => (
                      <tr key={i} className="border-b last:border-0">{verify.fields.map((f) => { const idx = headerIndex(mapping[f.field]); return <td key={f.field} className="px-3 py-1.5">{idx >= 0 ? (row[idx] || "") : "—"}</td>; })}</tr>
                    ))}</tbody>
                  </table>
                </div>
              )}

              <div className="flex items-center gap-3">
                <Button onClick={doImport} disabled={importing} data-testid="gs-import">{importing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}Import with this Mapping</Button>
                <label className="flex items-center gap-1.5 text-xs cursor-pointer"><input type="checkbox" checked={saveDefault} onChange={(e) => setSaveDefault(e.target.checked)} data-testid="gs-save-default" />Save as Default</label>
              </div>
            </div>
          )}
        </div>
      )}

      <Button variant="ghost" size="sm" onClick={downloadTemplate} data-testid="gs-download-template-2"><Download className="w-3.5 h-3.5 mr-1" />Download Lead Template (CSV)</Button>
    </div>
  );
}

export default GoogleSheetsPanel;
